import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { useAuthentication } from '@features/auth/context'
import { useLogin } from '@services/auth/mutations/useLogin'

import { AuthLayout } from '../components/AuthLayout'
import { isSupportedLanguage, toI18nextCode } from '@/i18n/languages'

export function Login() {
  const { t, i18n } = useTranslation('auth')
  const { setUser } = useAuthentication()

  const [searchParams] = useSearchParams()

  // Handle language query parameter
  useEffect(() => {
    const langParam = searchParams.get('lang')
    if (isSupportedLanguage(langParam)) {
      i18n.changeLanguage(toI18nextCode(langParam))
      localStorage.setItem('preferred_language', langParam)
    }
  }, [searchParams, i18n])

  const { search } = useLocation()
  const navigate = useNavigate()
  const urlRedirect = new URLSearchParams(search).get('redirect') || '/'

  const login = useLogin()

  const schema = useMemo(
    () =>
      z.object({
        username: z.string().min(1, t('login.emailRequired')),
        password: z.string().min(1, t('login.passwordRequired'))
      }),
    [t]
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' }
  })

  function onSubmit(values: z.infer<typeof schema>) {
    login.mutate({
      body: values,
      params: {
        onSuccess: (res) => {
          setUser(res)
          navigate(urlRedirect, { replace: true })
        }
      }
    })
  }

  return (
    <AuthLayout
      title={t('login.title')}
      footer={
        <>
          {t('login.noAccount')}{' '}
          <Link
            to="/register"
            className="font-semibold text-primary hover:underline"
          >
            {t('login.register')}
          </Link>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('login.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder={t('login.email')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('login.password')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="current-password"
                    placeholder={t('login.password')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <div className="flex justify-end">
                  <Link
                    to="/forgotten-password"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={login.isPending}
          >
            {t('login.submit')}
          </Button>

        </form>
      </Form>
    </AuthLayout>
  )
}
